/**
 * studio-shop D6 — the shop checkout's payment (`shop-payment-step`): the card
 * panel the website's booking checkout uses, on the studio's own Stripe account,
 * paying the order's one chargeable payment. It offers what bookings offer
 * (the card form today; Apple Pay and Google Pay on the day bookings turn them
 * on). Once Stripe takes the payment, the order page opens and confirms it.
 * A money piece: a custom site may restyle it but never redraw it.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'shop-payment-step',
  description: "The shop checkout's payment: Stripe's card form on the studio's account, wallets where offered, and Pay.",
  drawing: 'standard-only',
  options: {},
  parts: {
    card: { description: "Stripe's payment form: wallets, a saved card, a new card.", required: true },
    notice: { description: 'Why the order cannot be paid by card right now.', required: true },
    decline: { description: 'Why the card was declined, above the button.', required: true },
    pay: { description: 'Pay with the total.', required: true },
    secure: { description: 'Paid securely through Stripe, and who the seller is.', required: false },
  },
  actions: {
    pay: { description: 'Confirms the payment with what is filled in.', args: [] },
  },
  events: {},
});

/** What the shop's payment step knows. */
export interface ShopPaymentStepState {
  /** The total drawn on the button: "$285.00". */
  readonly amount: string;
  /** True once the card form is filled in enough to pay. */
  readonly complete: boolean;
  /** True while a payment is being sent. */
  readonly paying: boolean;
  /** The decline in the card's words; null when none. */
  readonly decline: string | null;
  /** The way out after a decline; null when none. */
  readonly declineNote: string | null;
  /** Why the order cannot be paid by card now (card payments paused); null when the card form shows. */
  readonly notice: string | null;
  /** The studio's name: the seller. */
  readonly seller: string | null;
}

/** What a drawing can ask the shop's payment step to do. */
export interface ShopPaymentStepActions {
  /** Confirm the payment with what is filled in. */
  readonly pay: () => void;
}

/** The shop's payment step reports nothing to its host; the order page takes over once paid. */
export type ShopPaymentStepEvents = Record<string, never>;
